import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SlotDetails, useSlotCheck } from "@/components/review/SlotDetails"
import { dauerMin, naiv, type Vorschlag } from "@/components/review/TerminlinkAktion"

/**
 * Terminlink (Baustein 5, #124, Paul 29.09.2026): drei freie Termine werden
 * beim Senden als BLOCKER in HERO reserviert, der Kunde wählt per Link oder
 * nennt eigene Zeiten. Kanäle: SMS (mit WhatsApp-Haken des Betriebs
 * WhatsApp), E-Mail, oder nur Link zum Kopieren (z. B. in eine Antwort).
 * Keine HERO-IDs in der Anzeige — nur Bezeichnungen.
 */
type Vorschau = {
  moeglich: boolean; grund: string | null
  kunde?: { name: string; telefon: string; telefon_fest: string; email: string; street: string; zipcode: string; city: string }
  vorgang?: string; anliegen?: string; dauer_min?: number; vorschlaege?: string[]; text?: string
  sms_moeglich?: boolean; gueltig_stunden?: number
}

async function post(url: string, body: unknown) {
  const r = await fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`)
  return d
}

export function TerminlinkDialog({ tenant, projectId, onClose }: { tenant: string; projectId: number; onClose: () => void }) {
  const qc = useQueryClient()
  const v = useQuery<Vorschau>({
    queryKey: ["terminlink-vorschau", tenant, projectId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/terminlinks/vorschau?tenant=${encodeURIComponent(tenant)}&project_id=${projectId}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const [sms, setSms] = useState(true)
  const [mail, setMail] = useState(false)
  const [telefon, setTelefon] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [anliegen, setAnliegen] = useState<string | null>(null)
  const [ergebnis, setErgebnis] = useState<{ link: string; vorschlaege: string[]; versand: Record<string, string> } | null>(null)
  const d = v.data
  const tel = telefon ?? d?.kunde?.telefon ?? ""
  const mailAdr = email ?? d?.kunde?.email ?? ""
  const anl = anliegen ?? d?.anliegen ?? ""
  const kanaele = [sms && d?.sms_moeglich ? "sms" : null, mail ? "email" : null].filter(Boolean) as string[]
  const senden = useMutation({
    mutationFn: () => post("/api/v1/terminlinks", {
      tenant, project_id: projectId, kanaele: kanaele.length ? kanaele : ["link"],
      telefon: tel || null, email: mailAdr || null, anliegen: anl || null,
    }),
    onSuccess: (r) => { setErgebnis(r); qc.invalidateQueries({ queryKey: ["terminlinks"] }) },
  })
  const box: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }
  const karte: React.CSSProperties = { background: "var(--bg-card, #fff)", borderRadius: 12, padding: 20, width: "min(560px, 94vw)", maxHeight: "90vh", overflowY: "auto" }
  const lbl: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, margin: "10px 0 4px" }
  return (
    <div className="pp-overlay" style={box} onClick={onClose}>
      <div style={karte} onClick={(e) => e.stopPropagation()} data-testid="terminlink-dialog">
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Terminvorschläge senden</div>
        {v.isLoading && <div className="text-muted-foreground text-sm">Suche freie Termine …</div>}
        {d && !d.moeglich && <div className="text-sm" style={{ color: "#b91c1c" }}>{d.grund}</div>}
        {d && d.moeglich && !ergebnis && (
          <>
            <div className="text-[12.5px] text-muted-foreground">
              {d.vorgang} · {d.kunde?.name || "Kunde"}. Beim Senden werden diese Termine als BLOCKER reserviert
              ({d.gueltig_stunden} Stunden, danach Wiedervorlage):
            </div>
            <ul className="text-[13px]" style={{ margin: "8px 0 4px 18px", listStyle: "disc" }}>
              {(d.vorschlaege || []).map((s) => <li key={s}>{s}</li>)}
            </ul>
            <label style={lbl}>Anliegen (steht in der Nachricht)</label>
            <input className="pp-input w-full" value={anl} onChange={(e) => setAnliegen(e.target.value)} />
            <label style={{ ...lbl, display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={sms && !!d.sms_moeglich} disabled={!d.sms_moeglich}
                     onChange={(e) => setSms(e.target.checked)} data-testid="kanal-sms" />
              Per SMS {d.sms_moeglich ? "" : "(für diesen Betrieb nicht eingeschaltet)"}
            </label>
            {sms && d.sms_moeglich && <input className="pp-input w-full" value={tel} placeholder="Handynummer" onChange={(e) => setTelefon(e.target.value)} />}
            <label style={{ ...lbl, display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} data-testid="kanal-email" />
              Per E-Mail
            </label>
            {mail && <input className="pp-input w-full" value={mailAdr} placeholder="E-Mail-Adresse" onChange={(e) => setEmail(e.target.value)} />}
            <div className="text-[12px] text-muted-foreground" style={{ marginTop: 10, whiteSpace: "pre-wrap", background: "var(--bg-muted, #f8fafc)", padding: 10, borderRadius: 8 }}>
              {d.text}
            </div>
            {!kanaele.length && <div className="text-[12px]" style={{ marginTop: 6 }}>Ohne Kanal wird nur der Link erzeugt — zum Kopieren, z. B. in eine Antwort.</div>}
            {senden.error && <div className="text-sm" style={{ color: "#b91c1c", marginTop: 8 }}>{(senden.error as Error).message}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" className="pp-btn" onClick={onClose}>Abbrechen</button>
              <button type="button" className="pp-btn pp-btn-primary" disabled={senden.isPending} onClick={() => senden.mutate()}>
                {senden.isPending ? "Reserviere …" : kanaele.length ? "Reservieren und senden" : "Reservieren und Link erzeugen"}
              </button>
            </div>
          </>
        )}
        {ergebnis && (
          <div data-testid="terminlink-ergebnis">
            <div className="text-sm" style={{ marginBottom: 6 }}>Reserviert: {ergebnis.vorschlaege.join(" · ")}</div>
            <div className="text-[12px] text-muted-foreground">
              {Object.entries(ergebnis.versand).map(([k, s]) => `${k === "sms" ? "SMS/WhatsApp" : "E-Mail"}: ${s}`).join(" · ") || "Nur Link erzeugt"}
            </div>
            <input className="pp-input w-full" readOnly value={ergebnis.link} style={{ marginTop: 8 }} onFocus={(e) => e.target.select()} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" className="pp-btn" onClick={() => navigator.clipboard?.writeText(ergebnis.link)}>Link kopieren</button>
              <button type="button" className="pp-btn pp-btn-primary" onClick={onClose}>Fertig</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

type Eintrag = {
  id: number; betrieb: string; status: string; status_text: string; braucht_buero: boolean
  kunde: string; telefon: string; email: string; vorgang: string; vorschlaege: string[]; gewaehlt: string | null
  wuensche: string; korrekturen: Record<string, string>; kanaele: string[]; dauer_min: number | null
  erstellt_at: string | null; geoeffnet_at: string | null; erledigt_at: boolean; link: string | null
  tenant_slug?: string
  /** Rohdaten der Vorschläge (HERO-Format) — für die Slot-/Blocker-Ansicht. */
  slots?: (Vorschlag & { event_id?: number | null })[]
  gewaehlt_index?: number | null
}

/** Abstimmung mit mehreren Personen (Stufe 2, Paul 01.10.2026). */
type GruppenSlot = Vorschlag & { event_id?: number | null; raus?: boolean }
type GruppenPerson = {
  id: number; name: string; telefon: string; email: string; status: string; passt: number[]; wuensche: string
  kanaele: string[]; geoeffnet: boolean; erinnert: boolean; korrekturen: Record<string, string>
}
type Gruppe = {
  id: number; betrieb: string; tenant_slug: string; status: string; status_text: string; braucht_buero: boolean
  vorgang: string; anliegen: string; runde: number; dauer_min: number | null; slots: GruppenSlot[]
  gemeinsam: number[]; termin: string | null; info_an: string; personen: GruppenPerson[]; erledigt_at: boolean
}

const kurz = (s: GruppenSlot) => (s.label || naiv(s.start)).replace(/, \d{2}\.\d{2}\.(\d{4})/, (m) => m.slice(0, -5))

function AbstimmungZeile({ g, onAktion, laeuft }: {
  g: Gruppe; laeuft: boolean
  onAktion: (was: "bestaetigen" | "ablehnen" | "neue-runde" | "erledigt", body?: unknown) => void
}) {
  const [wahl, setWahl] = useState<number | null>(g.gemeinsam[0] ?? null)
  const [zeit, setZeit] = useState("")
  const [runde, setRunde] = useState<string[]>(["", "", ""])
  const [rundeOffen, setRundeOffen] = useState(false)
  const [blocker, setBlocker] = useState(false)
  const aktiv = !g.erledigt_at && !["bestaetigt", "abgelehnt", "zurueckgezogen"].includes(g.status)
  const zeitIso = (lokal: string) => {
    const dt = new Date(lokal)
    const off = -dt.getTimezoneOffset()
    const v = off >= 0 ? "+" : "-"
    return `${lokal}:00${v}${String(Math.floor(Math.abs(off) / 60)).padStart(2, "0")}:${String(Math.abs(off) % 60).padStart(2, "0")}`
  }
  const zelle = (p: GruppenPerson, i: number, s: GruppenSlot) => {
    if (p.status !== "abgestimmt") return { t: "…", c: "#888780", title: "noch keine Antwort" }
    if (p.passt.includes(i)) return { t: "✓", c: "#166534", title: "passt" }
    return { t: "✗", c: s.raus ? "#991b1b" : "#991b1b", title: "passt nicht" }
  }
  const gewaehlt = wahl != null ? g.slots[wahl] : null
  return (
    <li className="border-t py-2 first:border-t-0" data-testid={`abstimmung-${g.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={g.braucht_buero ? "rounded px-1.5 py-0.5 bg-amber-100 text-amber-900" : "rounded px-1.5 py-0.5 bg-slate-100 text-slate-700"}>{g.status_text}</span>
        <span className="font-medium">Abstimmung: {g.personen.map((p) => p.name || "Person").join(" · ")}</span>
        <span className="text-muted-foreground">{[g.vorgang, g.betrieb, g.runde > 1 ? `Runde ${g.runde}` : ""].filter(Boolean).join(" · ")}</span>
      </div>
      {g.termin && <div style={{ marginTop: 2 }}>Termin: <b>{g.termin}</b></div>}
      <div style={{ overflowX: "auto", marginTop: 6 }}>
        <table className="text-[12px]" style={{ borderCollapse: "collapse" }} data-testid={`abstimmung-raster-${g.id}`}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "2px 8px 2px 0" }} />
              {g.slots.map((s, i) => (
                <th key={i} style={{ padding: "2px 6px", fontWeight: 500, whiteSpace: "nowrap",
                  color: s.raus ? "#888780" : g.gemeinsam.includes(i) ? "#166534" : "inherit",
                  textDecoration: s.raus ? "line-through" : "none" }}>{kurz(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {g.personen.map((p) => (
              <tr key={p.id}>
                <td style={{ padding: "2px 8px 2px 0", whiteSpace: "nowrap" }}>{p.name || "Person"}</td>
                {g.slots.map((s, i) => {
                  const z = zelle(p, i, s)
                  return <td key={i} title={z.title} style={{ textAlign: "center", color: z.c, padding: "2px 6px" }}>{z.t}</td>
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="text-muted-foreground" style={{ marginTop: 4 }}>
        {g.personen.map((p) => (
          <li key={p.id}>
            <b style={{ color: "var(--text)" }}>{p.name || "Person"}</b>:{" "}
            {p.status === "abgestimmt" ? (p.passt.length ? `${p.passt.length} Termin(e) passen` : "keiner passt")
              : p.status === "abgelaufen" ? "hat nicht geantwortet"
              : `noch keine Antwort${p.geoeffnet ? " (Link geöffnet)" : ""}${p.erinnert ? " · erinnert" : ""}`}
            {p.wuensche && <> · Wunsch: „{p.wuensche}“</>}
            {p.status !== "abgestimmt" && p.telefon && <> · <a className="underline" href={`tel:${p.telefon}`}>{p.telefon}</a></>}
          </li>
        ))}
      </ul>
      {aktiv && g.status === "gemeinsam" && (
        <div style={{ marginTop: 6 }}>
          <div className="flex flex-wrap gap-3">
            {g.gemeinsam.map((i) => (
              <label key={i} className="inline-flex items-center gap-1">
                <input type="radio" name={`gem-${g.id}`} checked={wahl === i} onChange={() => setWahl(i)} />
                {g.slots[i]?.label}
              </label>
            ))}
          </div>
          {gewaehlt && (
            <SlotAnsicht tenant={g.tenant_slug} start={naiv(gewaehlt.start)} dauer={dauerMin(gewaehlt)}
              partnerId={gewaehlt.partner_ids?.[0]} reserviert />
          )}
          <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 4 }}>
            <button type="button" className="underline" disabled={laeuft || wahl == null}
              onClick={() => onAktion("bestaetigen", { index: wahl })}>Bestätigen (an alle)</button>
            <button type="button" className="underline text-red-800" disabled={laeuft} onClick={() => onAktion("ablehnen")}>Absagen</button>
          </div>
        </div>
      )}
      {aktiv && g.status !== "gemeinsam" && (
        <div style={{ marginTop: 6 }}>
          {g.status !== "offen" && (
            <div className="flex flex-wrap items-center gap-3">
              <input type="datetime-local" className="pp-input h-7 py-0 text-[12px]" value={zeit}
                onChange={(e) => setZeit(e.target.value)} aria-label="Termin für alle" />
              <button type="button" className="underline" disabled={laeuft || !zeit}
                onClick={() => onAktion("bestaetigen", { start: zeitIso(zeit) })}>Eintragen und bestätigen (an alle)</button>
              <button type="button" className="underline" onClick={() => setRundeOffen((v) => !v)}>Neue Runde</button>
              <button type="button" className="underline text-red-800" disabled={laeuft} onClick={() => onAktion("ablehnen")}>Absagen</button>
              {g.status === "abgelaufen" && <button type="button" className="underline" disabled={laeuft} onClick={() => onAktion("erledigt")}>Angerufen, erledigt</button>}
            </div>
          )}
          {zeit && <SlotAnsicht tenant={g.tenant_slug} start={`${zeit}:00`} dauer={g.dauer_min || 60} />}
          {rundeOffen && (
            <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 6 }} data-testid={`abstimmung-runde-${g.id}`}>
              <span className="text-muted-foreground">Neue Vorschläge (leer = automatisch):</span>
              {runde.map((v, i) => (
                <input key={i} type="datetime-local" className="pp-input h-7 py-0 text-[12px]" value={v}
                  onChange={(e) => setRunde(runde.map((x, j) => (j === i ? e.target.value : x)))} />
              ))}
              <button type="button" className="underline" disabled={laeuft}
                onClick={() => onAktion("neue-runde", { starts: runde.filter(Boolean).map((v) => `${v}:00`) })}>
                An alle senden
              </button>
            </div>
          )}
          {g.status === "offen" && (
            <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
              <button type="button" className="underline" onClick={() => setBlocker((v) => !v)}>
                {blocker ? "Blocker ausblenden" : "Blocker im Kalender ansehen"}
              </button>
              <button type="button" className="underline" disabled={laeuft} onClick={() => onAktion("erledigt")}>Zurückziehen</button>
            </div>
          )}
          {g.status === "offen" && blocker && g.slots.filter((s) => !s.raus).map((s, i) => (
            <div key={i} style={{ marginTop: 6 }}>
              <div style={{ fontWeight: 500 }}>{s.label}</div>
              <SlotAnsicht tenant={g.tenant_slug} start={naiv(s.start)} dauer={dauerMin(s)} partnerId={s.partner_ids?.[0]} reserviert />
            </div>
          ))}
        </div>
      )}
    </li>
  )
}

/** Slot-/Blocker-Ansicht wie in der Plan-Karte (Paul 01.10.2026: „kannst du
 *  auch hier dann nochmal die slot / block ansicht einbringen?"). `start` =
 *  naive Berlin-Zeit. Ohne Monteur-Wahl: gebucht wird mit dem Monteur des
 *  Blockers bzw. dem freien, den das Backend beim Bestätigen nimmt. */
function SlotAnsicht({ tenant, start, dauer, partnerId, reserviert }: {
  tenant: string; start: string; dauer: number; partnerId?: number | null; reserviert?: boolean
}) {
  const check = useSlotCheck({ tenant }, start, dauer, partnerId)
  return (
    <div style={{ maxWidth: 560 }} data-testid="terminlink-slotansicht">
      <SlotDetails slot={{ start_datetime: start, duration_minutes: dauer, partner_id: partnerId ?? null }}
        slotCheck={check} reserviert={reserviert} />
    </div>
  )
}

/**
 * Büro-Liste: „Kunde hat gewählt“ → Bestätigen/Ablehnen, „Kunde wünscht andere
 * Zeit“ → Termin eintragen (Datum/Uhrzeit), „Keine Reaktion“ → anrufen und
 * erledigen. Die Nachricht an den Kunden geht beim Bestätigen/Ablehnen
 * automatisch raus.
 */
export function TerminlinksBlock({ tenant }: { tenant: string }) {
  const qc = useQueryClient()
  const q = useQuery<{ eintraege: Eintrag[]; gruppen?: Gruppe[]; offen: number }>({
    queryKey: ["terminlinks", tenant],
    queryFn: async () => {
      const p = new URLSearchParams({ tage: "30" })
      if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/terminlinks?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    refetchInterval: 60_000,
  })
  const [wunschZeit, setWunschZeit] = useState<Record<number, string>>({})
  const [blockerOffen, setBlockerOffen] = useState<Record<number, boolean>>({})
  const [fehler, setFehler] = useState<string | null>(null)
  const aktion = useMutation({
    mutationFn: ({ id, was, start }: { id: number; was: "bestaetigen" | "ablehnen" | "erledigt"; start?: string }) =>
      post(`/api/v1/terminlinks/${id}/${was}`, was === "bestaetigen" ? { start: start || null } : {}),
    onSuccess: () => { setFehler(null); qc.invalidateQueries({ queryKey: ["terminlinks"] }) },
    onError: (e) => setFehler(e instanceof Error ? e.message : String(e)),
  })
  const gruppenAktion = useMutation({
    mutationFn: ({ id, was, body }: { id: number; was: string; body?: unknown }) =>
      post(`/api/v1/terminlinks/gruppen/${id}/${was}`, body ?? {}),
    onSuccess: () => { setFehler(null); qc.invalidateQueries({ queryKey: ["terminlinks"] }) },
    onError: (e) => setFehler(e instanceof Error ? e.message : String(e)),
  })
  const d = q.data
  const gruppen = [...(d?.gruppen || [])].sort((a, b) => Number(b.braucht_buero) - Number(a.braucht_buero))
  if (!d || !Array.isArray(d.eintraege) || (d.eintraege.length === 0 && gruppen.length === 0)) return null
  const sortiert = [...d.eintraege].sort((a, b) => Number(b.braucht_buero) - Number(a.braucht_buero))
  const zeitIso = (lokal: string) => {
    const dt = new Date(lokal)
    const off = -dt.getTimezoneOffset()
    const s = off >= 0 ? "+" : "-"
    const hh = String(Math.floor(Math.abs(off) / 60)).padStart(2, "0"), mm = String(Math.abs(off) % 60).padStart(2, "0")
    return `${lokal}:00${s}${hh}:${mm}`
  }
  return (
    <div className="pp-card" style={{ padding: 16, marginBottom: 16 }} data-testid="terminlinks">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Terminlinks {d.offen ? `(${d.offen} zu erledigen)` : ""}</div>
      {fehler && <div className="text-sm" style={{ color: "#b91c1c" }}>{fehler}</div>}
      {gruppen.length > 0 && (
        <ul className="text-[12.5px]" style={{ marginBottom: 8 }}>
          {gruppen.map((g) => (
            <AbstimmungZeile key={g.id} g={g} laeuft={gruppenAktion.isPending}
              onAktion={(was, body) => gruppenAktion.mutate({ id: g.id, was, body })} />
          ))}
        </ul>
      )}
      <ul className="text-[12.5px]">
        {sortiert.map((e) => (
          <li key={e.id} className="border-t py-2 first:border-t-0" data-testid={`terminlink-${e.id}`}>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={e.braucht_buero ? "rounded px-1.5 py-0.5 bg-amber-100 text-amber-900" : "rounded px-1.5 py-0.5 bg-slate-100 text-slate-700"}>{e.status_text}</span>
              <span className="font-medium">{e.kunde || "Kunde"}</span>
              <span className="text-muted-foreground">{[e.vorgang, e.betrieb].filter(Boolean).join(" · ")}</span>
            </div>
            <div className="text-muted-foreground" style={{ marginTop: 2 }}>
              {e.gewaehlt ? <>Gewählt: <b style={{ color: "inherit" }}>{e.gewaehlt}</b></> : <>Vorschläge: {e.vorschlaege.join(" · ")}</>}
              {e.wuensche && <> · Wunsch: „{e.wuensche}“</>}
              {Object.keys(e.korrekturen || {}).length > 0 && <> · Angaben geändert: {Object.entries(e.korrekturen).map(([k, v]) => `${k}: ${v}`).join(", ")}</>}
            </div>
            {e.tenant_slug && e.braucht_buero && !e.erledigt_at && e.status === "gewaehlt"
              && e.gewaehlt_index != null && e.slots?.[e.gewaehlt_index] && (() => {
                const s = e.slots[e.gewaehlt_index]
                return <SlotAnsicht tenant={e.tenant_slug} start={naiv(s.start)} dauer={dauerMin(s)}
                  partnerId={s.partner_ids?.[0]} reserviert />
              })()}
            {e.tenant_slug && (e.status === "wunsch" || e.status === "abgelaufen") && !e.erledigt_at && wunschZeit[e.id] && (
              <SlotAnsicht tenant={e.tenant_slug} start={`${wunschZeit[e.id]}:00`} dauer={e.dauer_min || 60} />
            )}
            {e.braucht_buero && !e.erledigt_at && (
              <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 4 }}>
                {e.status === "gewaehlt" && <>
                  <button type="button" className="underline" disabled={aktion.isPending} onClick={() => aktion.mutate({ id: e.id, was: "bestaetigen" })}>Bestätigen</button>
                  <button type="button" className="underline text-red-800" disabled={aktion.isPending} onClick={() => aktion.mutate({ id: e.id, was: "ablehnen" })}>Ablehnen</button>
                </>}
                {(e.status === "wunsch" || e.status === "abgelaufen") && <>
                  <input type="datetime-local" className="pp-input h-7 py-0 text-[12px]" value={wunschZeit[e.id] || ""}
                         onChange={(ev) => setWunschZeit({ ...wunschZeit, [e.id]: ev.target.value })} aria-label="Termin" />
                  <button type="button" className="underline" disabled={aktion.isPending || !wunschZeit[e.id]}
                          onClick={() => aktion.mutate({ id: e.id, was: "bestaetigen", start: zeitIso(wunschZeit[e.id]) })}>Eintragen und bestätigen</button>
                  {e.status === "wunsch" && <button type="button" className="underline text-red-800" disabled={aktion.isPending} onClick={() => aktion.mutate({ id: e.id, was: "ablehnen" })}>Absagen</button>}
                </>}
                {e.telefon && <a className="underline" href={`tel:${e.telefon}`}>{e.telefon}</a>}
                {e.status === "abgelaufen" && <button type="button" className="underline" disabled={aktion.isPending} onClick={() => aktion.mutate({ id: e.id, was: "erledigt" })}>Angerufen, erledigt</button>}
              </div>
            )}
            {e.status === "gesendet" && (
              <div className="flex flex-wrap items-center gap-3 text-muted-foreground" style={{ marginTop: 4 }}>
                <span>{e.geoeffnet_at ? "Link geöffnet" : "Link noch nicht geöffnet"}</span>
                {e.tenant_slug && (e.slots?.length ?? 0) > 0 && (
                  <button type="button" className="underline" onClick={() => setBlockerOffen({ ...blockerOffen, [e.id]: !blockerOffen[e.id] })}>
                    {blockerOffen[e.id] ? "Blocker ausblenden" : "Blocker im Kalender ansehen"}
                  </button>
                )}
                <button type="button" className="underline" disabled={aktion.isPending} onClick={() => aktion.mutate({ id: e.id, was: "erledigt" })}>Zurückziehen</button>
              </div>
            )}
            {e.status === "gesendet" && blockerOffen[e.id] && e.tenant_slug && (e.slots || []).map((s, i) => (
              <div key={i} style={{ marginTop: 6 }}>
                <div style={{ fontWeight: 500 }}>{s.label}</div>
                <SlotAnsicht tenant={e.tenant_slug!} start={naiv(s.start)} dauer={dauerMin(s)} partnerId={s.partner_ids?.[0]} reserviert />
              </div>
            ))}
          </li>
        ))}
      </ul>
    </div>
  )
}
